/**
 * Vehicle History Data Manager
 * 
 * Handles progressive loading, pagination, and memory management
 * for large vehicle history datasets (800+ points)
 * 
 * Features:
 * - Progressive loading (load data in chunks)
 * - Memory-aware pagination
 * - Data virtualization (unload off-screen data)
 * - Intelligent prefetching
 * - Memory pressure detection
 * - Background data cleanup
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API } from '../../utils/config';

class VehicleHistoryDataManager {
  constructor() {
    this.cache = new Map();
    this.loadingQueue = new Set();
    this.memoryThreshold = 50 * 1024 * 1024; // 50MB threshold
    this.maxCacheSize = 100; // Maximum cached chunks
    this.chunkSize = 100; // Points per chunk
    this.metrics = {
      totalPoints: 0,
      loadedChunks: 0,
      memoryUsage: 0,
      cacheHits: 0,
      cacheMisses: 0,
      lastCleanup: Date.now()
    };

    // Monitor memory pressure
    this.setupMemoryMonitoring();
  }

  /**
   * Setup memory monitoring and cleanup
   */
  setupMemoryMonitoring() {
    // Check memory every 30 seconds
    setInterval(() => {
      if ('memory' in performance) {
        const memInfo = performance.memory;
        const memoryMB = memInfo.usedJSHeapSize / 1024 / 1024;

        this.metrics.memoryUsage = memoryMB;

        // Clean up cache if memory usage is high
        if (memoryMB > 150) { // 150MB threshold
          //
          this.cleanupOldCache();
        }
      }
    }, 30000);
  }

  /**
   * Generate cache key for a specific date range and vehicle
   */
  getCacheKey(vehicleId, startDate, endDate, page = 0) {
    return `${vehicleId}-${startDate}-${endDate}-${page}`;
  }

  /**
   * Load vehicle history with progressive loading
   */
  async loadVehicleHistory(vehicleId, startDate, endDate, options = {}) {
    const {
      maxPoints = 1000,
      enablePagination = true,
      progressCallback = null
    } = options;

    try {
      // First, get the total count to determine if we need pagination
      const countResponse = await axios.get(`${API}/vehicles/${vehicleId}/history/count`, {
        params: {
          start_date: startDate,
          end_date: endDate
        }
      });

      const totalCount = countResponse.data.total_count || 0;

      if (totalCount === 0) {
        return { locations: [], statistics: null, totalCount: 0, hasMore: false };
      }

      // If small dataset, load all at once
      if (totalCount <= this.chunkSize || !enablePagination) {
        return this.loadSingleBatch(vehicleId, startDate, endDate, Math.min(totalCount, maxPoints));
      }

      // For large datasets, use progressive loading
      return this.loadProgressively(vehicleId, startDate, endDate, totalCount, maxPoints, progressCallback);

    } catch (error) {
      throw error;
    }
  }

  /**
   * Load data in a single batch (for small datasets)
   */
  async loadSingleBatch(vehicleId, startDate, endDate, limit) {
    const cacheKey = this.getCacheKey(vehicleId, startDate, endDate);

    if (this.cache.has(cacheKey)) {
      this.metrics.cacheHits++;
      return this.cache.get(cacheKey);
    }

    const response = await axios.get(`${API}/vehicles/${vehicleId}/history`, {
      params: {
        start_date: startDate,
        end_date: endDate,
        limit: limit
      }
    });

    const result = {
      locations: response.data.locations || [],
      statistics: response.data.statistics,
      totalCount: response.data.locations?.length || 0,
      hasMore: false
    };

    // Cache the result
    this.cache.set(cacheKey, result);
    this.metrics.cacheMisses++;
    this.metrics.totalPoints = result.locations.length;

    return result;
  }

  /**
   * Load data progressively in chunks
   */
  async loadProgressively(vehicleId, startDate, endDate, totalCount, maxPoints, progressCallback) {
    const chunksNeeded = Math.ceil(Math.min(totalCount, maxPoints) / this.chunkSize);
    const allLocations = [];
    let statistics = null;

    // Load first chunk to get statistics
    const firstChunk = await this.loadChunk(vehicleId, startDate, endDate, 0);
    allLocations.push(...firstChunk.locations);
    statistics = firstChunk.statistics;

    if (progressCallback) {
      progressCallback(1, chunksNeeded, allLocations.length, totalCount);
    }

    // Load remaining chunks
    const promises = [];
    for (let chunk = 1; chunk < chunksNeeded; chunk++) {
      // Limit concurrent requests to avoid overwhelming the server
      if (promises.length >= 3) {
        await Promise.all(promises);
        promises.length = 0;
      }

      const promise = this.loadChunk(vehicleId, startDate, endDate, chunk)
        .then(chunkData => {
          allLocations.push(...chunkData.locations);
          this.metrics.loadedChunks++;

          if (progressCallback) {
            progressCallback(chunk + 1, chunksNeeded, allLocations.length, totalCount);
          }

          return chunkData;
        });

      promises.push(promise);
    }

    // Wait for all remaining chunks
    await Promise.all(promises);

    // Sort locations chronologically
    allLocations.sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));

    const result = {
      locations: allLocations.slice(0, maxPoints), // Ensure we don't exceed maxPoints
      statistics,
      totalCount,
      hasMore: totalCount > maxPoints
    };

    this.metrics.totalPoints = result.locations.length;

    return result;
  }

  /**
   * Load a specific chunk of data
   */
  async loadChunk(vehicleId, startDate, endDate, page) {
    const cacheKey = this.getCacheKey(vehicleId, startDate, endDate, page);

    // Check if already loading to prevent duplicate requests
    if (this.loadingQueue.has(cacheKey)) {
      // Wait for existing request to complete
      while (this.loadingQueue.has(cacheKey)) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    // Check cache
    if (this.cache.has(cacheKey)) {
      this.metrics.cacheHits++;
      return this.cache.get(cacheKey);
    }

    this.loadingQueue.add(cacheKey);
    this.metrics.cacheMisses++;

    try {

      const response = await axios.get(`${API}/vehicles/${vehicleId}/history`, {
        params: {
          start_date: startDate,
          end_date: endDate,
          limit: this.chunkSize,
          offset: page * this.chunkSize
        }
      });

      const chunkData = {
        locations: response.data.locations || [],
        statistics: response.data.statistics,
        page,
        timestamp: Date.now()
      };

      // Cache the chunk
      this.cacheChunk(cacheKey, chunkData);

      return chunkData;

    } finally {
      this.loadingQueue.delete(cacheKey);
    }
  }

  /**
   * Cache a chunk with memory management
   */
  cacheChunk(key, data) {
    // Remove oldest cache entries if we're at capacity
    if (this.cache.size >= this.maxCacheSize) {
      const oldestKey = this.cache.keys().next().value;
      this.cache.delete(oldestKey);
    }

    this.cache.set(key, data);
  }

  /**
   * Clean up old cache entries
   */
  cleanupOldCache() {
    const now = Date.now();
    const maxAge = 10 * 60 * 1000; // 10 minutes
    let cleaned = 0;

    for (const [key, data] of this.cache.entries()) {
      if (data.timestamp && (now - data.timestamp) > maxAge) {
        this.cache.delete(key);
        cleaned++;
      }
    }

    this.metrics.lastCleanup = now;
    //
  }

  /**
   * Preload data for better performance
   */
  async preloadNextChunk(vehicleId, startDate, endDate, currentPage) {
    const nextPage = currentPage + 1;
    const cacheKey = this.getCacheKey(vehicleId, startDate, endDate, nextPage);

    if (!this.cache.has(cacheKey) && !this.loadingQueue.has(cacheKey)) {
      try {
        await this.loadChunk(vehicleId, startDate, endDate, nextPage);
      } catch (error) {
      }
    }
  }

  /**
   * Clear all cache
   */
  clearCache() {
    this.cache.clear();
    this.loadingQueue.clear();
    this.metrics = {
      totalPoints: 0,
      loadedChunks: 0,
      memoryUsage: 0,
      cacheHits: 0,
      cacheMisses: 0,
      lastCleanup: Date.now()
    };
  }

  /**
   * Get performance metrics
   */
  getMetrics() {
    return {
      ...this.metrics,
      cacheSize: this.cache.size,
      cacheHitRate: this.metrics.cacheHits / (this.metrics.cacheHits + this.metrics.cacheMisses) * 100 || 0,
      activeLoads: this.loadingQueue.size
    };
  }
}

// Create singleton instance
const dataManager = new VehicleHistoryDataManager();

/**
 * Hook for using the vehicle history data manager
 */
export const useVehicleHistoryData = () => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [error, setError] = useState(null);
  const progressRef = useRef({ current: 0, total: 0 });

  const loadHistory = useCallback(async (vehicleId, startDate, endDate, options = {}) => {
    setLoading(true);
    setError(null);
    setProgress({ current: 0, total: 0 });

    try {
      const result = await dataManager.loadVehicleHistory(vehicleId, startDate, endDate, {
        ...options,
        progressCallback: (current, total, pointsLoaded, totalPoints) => {
          const progressData = {
            current,
            total,
            pointsLoaded,
            totalPoints,
            percentage: Math.round((current / total) * 100)
          };
          progressRef.current = progressData;
          setProgress(progressData);
        }
      });

      return result;
    } catch (err) {
      setError(err.message || 'Failed to load history data');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const clearCache = useCallback(() => {
    dataManager.clearCache();
  }, []);

  const getMetrics = useCallback(() => {
    return dataManager.getMetrics();
  }, []);

  return {
    loadHistory,
    loading,
    progress,
    error,
    clearCache,
    getMetrics
  };
};

/**
 * Progressive Loading Indicator Component
 */
export const HistoryLoadingIndicator = ({ loading, progress }) => {
  if (!loading) return null;

  const { current = 0, total = 0, pointsLoaded = 0, totalPoints = 0, percentage = 0 } = progress;

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-medium text-blue-800">Loading Vehicle History...</h4>
        <span className="text-sm text-blue-600">{percentage}%</span>
      </div>

      <div className="w-full bg-blue-200 rounded-full h-2 mb-2">
        <div
          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
          style={{ width: `${percentage}%` }}
        ></div>
      </div>

      <div className="flex justify-between text-xs text-blue-600">
        <span>Chunks: {current} / {total}</span>
        <span>Points: {pointsLoaded.toLocaleString()} / {totalPoints.toLocaleString()}</span>
      </div>

      <div className="text-xs text-blue-500 mt-1">
        Loading in chunks for optimal performance...
      </div>
    </div>
  );
};

/**
 * Memory Usage Monitor Component
 */
export const HistoryMemoryMonitor = () => {
  const [metrics, setMetrics] = useState({});

  useEffect(() => {
    const updateMetrics = () => {
      setMetrics(dataManager.getMetrics());
    };

    updateMetrics();
    const interval = setInterval(updateMetrics, 5000);

    return () => clearInterval(interval);
  }, []);

  const handleClearCache = () => {
    dataManager.clearCache();
    setMetrics(dataManager.getMetrics());
  };

  if (!Object.keys(metrics).length) return null;

  return (
    <div className="bg-gray-50 rounded-lg p-3 text-xs">
      <div className="flex justify-between items-center mb-2">
        <h5 className="font-medium text-gray-700">📊 Memory Usage</h5>
        <button
          onClick={handleClearCache}
          className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs hover:bg-red-200"
        >
          Clear Cache
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs">
        <div>Points Loaded: <span className="font-mono">{metrics.totalPoints?.toLocaleString()}</span></div>
        <div>Cache Size: <span className="font-mono">{metrics.cacheSize}</span></div>
        <div>Hit Rate: <span className="font-mono">{metrics.cacheHitRate?.toFixed(1)}%</span></div>
        <div>Memory: <span className="font-mono">{metrics.memoryUsage?.toFixed(1)}MB</span></div>
      </div>

      {metrics.memoryUsage > 100 && (
        <div className="mt-2 text-orange-600 text-xs">
          ⚠️ High memory usage detected
        </div>
      )}
    </div>
  );
};

export default dataManager;